import { PkButton, PkIcon, PkSpinner } from '@uefadigital/panenka-react';
import type {
  ColDef,
  GetRowIdParams,
  RowClassParams,
  RowClassRules,
  SortChangedEvent,
  SortDirection,
  ValueGetterParams,
} from 'ag-grid-community';
import useViewportSpy from 'beautiful-react-hooks/useViewportSpy';
import clsx from 'clsx';
import { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { useCoefficients } from '../../hooks/coefficients';
import { useCoefficientsColumns } from '../../hooks/columns';
import { useCoefficientsConfig } from '../../hooks/configuration';
import { useFilters } from '../../hooks/filters';
import { useSeasonYear } from '../../hooks/year';
import { handleExpandedToggleTracking } from '../../utils/tracking';

import Identifier from './Identifier';
import Position from './Position';
import Tooltip from './Tooltip';

import { useWindowSize } from '@hooks/useWindowSize';
import DataTableGrid from '@shared/DataTableGrid';
import { withErrorBoundary } from '@shared/ErrorBoundary';
import { useTranslateFn } from '@shared/Translate';
import type { Coefficient } from '@shared-types/coefficient.types';
import { CoefficientTrend } from '@shared-types/coefficient.types';
import { CompetitionType } from '@shared-types/competition.types';

import * as classes from './styles.module.scss';

const columnTypes: Record<string, ColDef> = {
  position: {
    cellClass: 'pk-pl--xs pk-justify-content--start ag-cell--position',
    cellDataType: 'object',
    cellRenderer: Position,
  },
  identifier: {
    cellDataType: 'object',
    cellRenderer: Identifier,
  },
} as const;

const defaultColDef: ColDef = {
  autoHeaderHeight: true,
  cellDataType: 'number',
  tooltipComponent: Tooltip,
  sortingOrder: ['desc', 'asc'],
  minWidth: 80,
  context: {
    useShortLabel: true,
  },
} as const;

const getRowId = (row: GetRowIdParams<Coefficient>) => row.data?.member?.id;

const Table = () => {
  const containerRef = useRef<HTMLDivElement>(null);
  const {
    configuration: { pageSize, competitionType, currentSeason, highlightPosition, tag },
  } = useCoefficientsConfig();
  const { columns } = useCoefficientsColumns();
  const { filters, filtersActive } = useFilters();
  const { coefficients, isLoading } = useCoefficients();
  const t = useTranslateFn();
  const [sorting, setSorting] = useState<{ name: string; order: SortDirection }>({
    name: 'position',
    order: 'asc',
  });
  const { isMobile, isDesktop } = useWindowSize();
  const { seasonYear } = useSeasonYear();

  const isClub = competitionType === CompetitionType.CLUB;

  const data = useMemo(
    () => coefficients.filter((c) => !filtersActive || c.member.countryCode === filters?.countryCode),
    [coefficients, filters?.countryCode, filtersActive],
  );

  const [expanded, setExpanded] = useState(false);
  const toggleExpanded = () =>
    setExpanded((current) => {
      if (current) {
        setTimeout(() => window.scrollTo({ top: containerRef.current.offsetTop - 100, behavior: 'smooth' }), 100);
      }
      handleExpandedToggleTracking(current, t(tag));
      return !current;
    });

  const handleSortingChange = (event: SortChangedEvent) => {
    const column = event.columns.find((c) => c.getSort());
    if (!column) return;

    setSorting({ name: column.getId(), order: column.getSort() });
  };

  const commonConditions = useMemo(
    () =>
      (sorting.name === 'avg' && sorting.order === 'desc') ||
      (sorting.name === 'position' && sorting.order === 'asc' && seasonYear === `${currentSeason}`),
    [sorting.name, sorting.order, seasonYear, currentSeason],
  );

  const isHighlighted = useCallback(
    ({ data: c }: RowClassParams<Coefficient>) => {
      if (!commonConditions) return false;
      const currentPosition = c.seasonRankings?.[0]?.position ?? NaN;
      return currentPosition === highlightPosition;
    },
    [commonConditions, highlightPosition],
  );

  const isHighlightedBg = useCallback(
    ({ data: c }: RowClassParams<Coefficient>) => {
      if (!commonConditions) return false;
      const currentPosition = c.seasonRankings?.[0]?.position ?? NaN;
      return currentPosition <= highlightPosition;
    },
    [commonConditions, highlightPosition],
  );

  const rowClassRules: RowClassRules = useMemo(
    () => ({
      [classes.highlighted]: isHighlighted,
      [classes.highlightedBg]: isHighlightedBg,
    }),
    [isHighlighted, isHighlightedBg],
  );

  const showTrend = useMemo(
    () => data.some((c) => c.overallRanking?.trend && c.overallRanking.trend !== CoefficientTrend.STABLE),
    [data],
  );

  const pinned = !isDesktop ? 'left' : undefined;
  const tableColumns = useMemo<ColDef[]>(
    () => [
      {
        field: 'position',
        headerName: 'position',
        minWidth: 0,
        width: 55,
        headerClass: 'ag-header-cell--position ag-header-cell--text-left',
        type: 'position',
        pinned,
        sort: 'asc',
        valueGetter: ({ data }: ValueGetterParams) => (data.overallRanking ?? data.seasonRankings?.at(0))?.position,
        cellRendererParams: {
          showTrend,
        },
      },
      {
        field: 'member',
        flex: 2,
        minWidth: isMobile ? 125 : 165,
        headerName: isClub ? 'club' : 'association',
        headerClass: 'ag-header-cell--identifier ag-header-cell--text-left',
        cellClass: clsx(isMobile && 'pk-pl--0 pk-pr--xs2', 'pk-justify-content--start'),
        type: 'identifier',
        pinned,
        sortable: false,
        context: {
          useShortLabel: false,
        },
      },
      ...columns.map<ColDef>((col) => ({
        ...col,
        flex: 1,
        headerClass: ({ colDef }) => colDef.headerTooltip && 'ag-header--tooltip',
        cellClass: ({ data }) => (col.context?.getBold?.(data) ? 'pk-font--base-bold' : ''),
      })),
    ],
    [pinned, showTrend, isMobile, isClub, columns],
  );

  const [visibleRows, setVisibleRows] = useState(pageSize);
  const expandPage = useCallback(
    () => setVisibleRows((current) => (current >= data.length ? data.length : current + pageSize)),
    [data.length, pageSize],
  );

  const expandBtnRef = useRef<HTMLDivElement>(null);
  const scrolled = useViewportSpy(expandBtnRef, { rootMargin: '25% 0px' });
  useEffect(() => {
    if (!expanded) {
      setVisibleRows(pageSize);
      return;
    }

    let interval: NodeJS.Timeout = null;
    if (!scrolled) {
      clearInterval(interval);
      return;
    }

    expandPage();
    interval = setInterval(() => {
      expandPage();
    }, 500);

    return () => {
      clearInterval(interval);
    };
  }, [expandPage, expanded, pageSize, scrolled]);

  return (
    <>
      <div className={classes.container} ref={containerRef}>
        {isLoading && (
          <div className={classes.loading}>
            <PkSpinner />
          </div>
        )}
        <DataTableGrid
          key={`data-table-${pinned && 'pinned'}`}
          className={classes.table}
          defaultColDef={defaultColDef}
          columnDefs={tableColumns}
          columnTypes={columnTypes}
          rowData={data}
          getRowId={getRowId}
          onSortChanged={handleSortingChange}
          rowClassRules={rowClassRules}
          tooltipShowDelay={0}
          tooltipInteraction
          sortable
          suppressMultiSort
          rowHeight={isMobile ? 48 : 64}
          pagination
          paginationPageSize={visibleRows}
          suppressPaginationPanel
        />
        {expanded && visibleRows < data.length && (
          <div className='pk-pt--m'>
            <PkSpinner size={32} />
          </div>
        )}
        {!isLoading && !data.length && (
          <p className='pk-text--center pk-text--text-02 pk-font-size--s'>
            {t('page_not_found_sorry')} {t('no_data_available')}
          </p>
        )}
      </div>
      <div ref={expandBtnRef}>
        {!!data.length && (expanded || visibleRows < data.length) && (
          <PkButton
            disabled={expanded && visibleRows < data.length}
            className='pk-mt--s pk-align-self--start'
            appearance='text'
            onPkClick={toggleExpanded}
          >
            {t(expanded ? 'see_less' : 'view_full_rankings')}
            <PkIcon name={`navigation-chevron-${expanded ? 'up' : 'down'}`} />
          </PkButton>
        )}
      </div>
    </>
  );
};

export { Table as PureTable };

export default withErrorBoundary(memo(Table));
